import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    AnalyticsRevenuesCards,
    AnalyticsViewsCards,
    SidebarDropdownSelection,
    ThemeOptions,
    ViewInitialState
} from "./types";
import { getUTCDate, setToEndOfDay, setToStartOfDay } from "helpers/time";
import { AnalyticsDateRangeOptions } from "views/page-content/analytics/AnalyticsPage";

const initialState = {
    analyticsViewsCardOrder: [
        AnalyticsViewsCards.Views,
        AnalyticsViewsCards.WatchTime,
        AnalyticsViewsCards.UniqueViewers
    ],
    analyticsRevenueCardOrder: [
        AnalyticsRevenuesCards.Revenue,
        AnalyticsRevenuesCards.Sales,
        AnalyticsRevenuesCards.Customers
    ],
    sidebarDropdownSelection: {
        videoCMS: false,
        creationTools: false,
        accountSettings: false,
        help: false
    },
    analyticsDateRangeSelection: AnalyticsDateRangeOptions.SevenDays,
    analyticsCustomDateRangeSelection: { start: undefined, end: undefined },
    theme: undefined
} as ViewInitialState;

/**
 * This store contains any persistent UI changes across the dashboard, except for the sidebar. Saves to local storage.
 */
export const view = createSlice({
    name: "view",
    initialState: initialState,
    reducers: {
        reorderAnalyticsViewsCardOrder: (
            state,
            { payload }: PayloadAction<AnalyticsViewsCards[]>
        ): ViewInitialState => {
            return {
                ...state,
                analyticsViewsCardOrder: payload
            };
        },
        reorderAnalyticsRevenueCardOrder: (
            state,
            { payload }: PayloadAction<AnalyticsRevenuesCards[]>
        ): ViewInitialState => {
            return {
                ...state,
                analyticsRevenueCardOrder: payload
            };
        },
        toggleSidebarDropdownSelection: (
            state,
            { payload }: PayloadAction<keyof SidebarDropdownSelection>
        ) => {
            return {
                ...state,
                sidebarDropdownSelection: {
                    ...state.sidebarDropdownSelection,
                    [payload]: !(
                        state.sidebarDropdownSelection?.[payload] ?? false
                    )
                }
            };
        },
        setSidebarDropdownSelection: (
            state,
            {
                payload
            }: PayloadAction<{
                key: keyof SidebarDropdownSelection;
                value: boolean;
            }>
        ) => {
            return {
                ...state,
                sidebarDropdownSelection: {
                    ...state.sidebarDropdownSelection,
                    [payload.key]: payload.value
                }
            };
        },
        updateAnalyticsDateRangeSelection: (
            state,
            { payload }: PayloadAction<AnalyticsDateRangeOptions>
        ): ViewInitialState => {
            return {
                ...state,
                analyticsDateRangeSelection: payload,
                analyticsCustomDateRangeSelection: {
                    start: null,
                    end: null
                }
            };
        },
        updateAnalyticsCustomStartDate: (
            state,
            { payload }: PayloadAction<Date>
        ): ViewInitialState => {
            return {
                ...state,
                analyticsCustomDateRangeSelection: {
                    ...state.analyticsCustomDateRangeSelection,
                    start: setToStartOfDay(getUTCDate(payload)).toISOString()
                }
            };
        },
        updateAnalyticsCustomEndDate: (
            state,
            { payload }: PayloadAction<Date>
        ): ViewInitialState => {
            return {
                ...state,
                analyticsCustomDateRangeSelection: {
                    ...state.analyticsCustomDateRangeSelection,
                    end: setToEndOfDay(getUTCDate(payload)).toISOString()
                }
            };
        },
        updateDashboardTheme: (
            state,
            { payload }: PayloadAction<ThemeOptions>
        ): ViewInitialState => {
            return {
                ...state,
                theme: payload
            };
        }
    }
});

export const {
    reorderAnalyticsViewsCardOrder,
    reorderAnalyticsRevenueCardOrder,
    toggleSidebarDropdownSelection,
    setSidebarDropdownSelection,
    updateAnalyticsDateRangeSelection,
    updateAnalyticsCustomStartDate,
    updateAnalyticsCustomEndDate,
    updateDashboardTheme
} = view.actions;

export default view.reducer;
